import { useQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useRef } from 'react';
import PostTopicListWithBreadcrumbs from './PostTopicListWithBreadcrumbs';
import PostTopicToast, { shouldShowToast } from './PostTopicToast';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { PostTopicListWithBreadcrumbs_TopicFragment } from '../../graphql/generated/graphql';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { UIActions } from '../../ui/uiSlice';
import populateObjects from '../../utils/populateObjects';
import { isAddOrShareMenuOpenAtom } from '../controlBar/controlBarMenuAtoms';
import getRemoveTopicDisabledMessage from '../sharing/getRemoveTopicDisabledMessage';
import {
  useAddTopicToPost,
  useRemoveTopicFromPost,
} from '../sharing/postPermissionOperations';

const POST_FRAGMENT = gql(`
  fragment PostTopicBar_post on Post {
    id
    publishedAt
    topics {
      id
    }
    ...getRemoveTopicDisabledMessage_post
  }
`);

const SERIES_FRAGMENT = gql(`
  fragment PostTopicBar_series on SeriesFields {
    id
    ...PostTopicListWithBreadcrumbs_postSeries
  }
`);

const EDIT_POST_QUERY = gql(/** @complexity 5 */ `
  query PostTopicBarEditing($id: ID!) {
    post(id: $id) {
      id
      ...useRemoveTopicFromPost_post
    }
  }
`);

type EventHandlerTopic = Parameters<
  typeof shouldShowToast
>[number]['newTopic'] &
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  React.ComponentProps<typeof PostTopicToast>['topic'];

interface PostTopicBarProps {
  editing: boolean;
  post: DocumentType<typeof POST_FRAGMENT>;
  series?: DocumentType<typeof SERIES_FRAGMENT> | null;
}

const PostTopicBar = ({ post, series, editing }: PostTopicBarProps) => {
  const allTopicMap = useAllTopicMap();
  const postTopics = populateObjects(
    allTopicMap,
    post.topics.map(({ id }) => id),
  );
  const user = useAppSelector((state) => state.session.user);
  const toastsRef = useRef<string[]>([]);
  const dispatch = useAppDispatch();
  const organizationName = useAppSelector(
    (state) => state.session.organization.name,
  );

  const { data } = useQuery(EDIT_POST_QUERY, {
    skip: !editing,
    variables: { id: post.id },
  });
  const postForEditing = data?.post;

  const removeTopic = useRemoveTopicFromPost();
  const addTopic = useAddTopicToPost();

  const dismissToasts = useCallback(
    () =>
      Promise.all(
        toastsRef.current.map((id) => dispatch(UIActions.removeToast(id))),
      ),
    [dispatch],
  );

  const isPublished = !!post.publishedAt;

  const getWarningCallback = async (
    topic: EventHandlerTopic,
    adding: boolean,
  ) => {
    if (
      shouldShowToast({
        oldTopics: postTopics,
        adding,
        newTopic: topic,
        organizationName,
        published: isPublished,
      })
    ) {
      await dismissToasts();
      // currently our redux typing doesn't consider thunks
      // eslint-disable-next-line  @typescript-eslint/await-thenable
      const id = (await dispatch(
        UIActions.pushToast(({ onDismiss }) => (
          <PostTopicToast
            topic={topic}
            adding={adding}
            addTopic={({ id: topicId }: { id: string }) =>
              addTopic(post.id, topicId)
            }
            removeTopic={(t: Parameters<typeof removeTopic>[1]) =>
              postForEditing && removeTopic(postForEditing, t)
            }
            organizationName={organizationName}
            published={isPublished}
            postTopics={postTopics}
            onDismiss={onDismiss}
          />
        )),
      )) as unknown;
      toastsRef.current.push(id as string);
    }
  };

  useEffect(
    () => () => {
      dismissToasts();
    },
    [dismissToasts],
  );

  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);

  const handleRemove = async (
    topic: PostTopicListWithBreadcrumbs_TopicFragment,
  ) => {
    if (postForEditing) {
      await removeTopic(postForEditing, topic);

      if (postForEditing.topics.length === 1) {
        setIsAddOrShareMenuOpen(true);
      }
    }
    getWarningCallback(topic, false);
  };

  const isEditing = editing && !!postForEditing;

  if (postTopics.length === 0 && !series) {
    return null;
  }

  return (
    <PostTopicListWithBreadcrumbs
      postId={post.id}
      postTopics={postTopics}
      postSeries={series}
      {...(isEditing
        ? {
            editing: true,
            onRemove: handleRemove,
            getRemoveTopicDisabledMessage: (topic) => {
              const fullTopic = allTopicMap[topic.id];
              if (!fullTopic) return undefined;
              return getRemoveTopicDisabledMessage(fullTopic, user, post);
            },
          }
        : { editing: false })}
    />
  );
};

export default PostTopicBar;
